import { AuditEvent as AuditEventQuery } from "src/auditLogs/queries";
import { AuditEvent } from "src/clients/taktile-api";

export type AuditLogActor = {
  id: string;
  name: string;
  type: "user" | "api_key";
};

const CLIENT_LABELS: Record<string, string> = {
  "mcp-server": "Acting through the MCP server",
  copilot: "Acting through the Copilot",
  "case-copilot": "Acting through the Case Copilot",
  agent: "Acting through a Taktile Agent",
};

export const clientLabel = (event: AuditEventQuery): string | undefined => {
  const client = (event.details as { client?: unknown } | undefined)?.client;

  if (typeof client !== "string" || client === "") {
    return undefined;
  }

  return CLIENT_LABELS[client] ?? `Acting through ${client}`;
};

export const buildActorFilterList = (
  userActors: AuditLogActor[] | null,
  apiKeyActors: AuditLogActor[] | null,
  eventData: AuditEventQuery[],
): AuditLogActor[] | null => {
  if (userActors === null || apiKeyActors === null) {
    return null;
  }

  const knownIds = new Set([...userActors, ...apiKeyActors].map((a) => a.id));

  const eventActors = eventData.reduce<AuditLogActor[]>((acc, event) => {
    if (
      !knownIds.has(event.actor_id) &&
      !acc.some((a) => a.id === event.actor_id)
    ) {
      acc.push({
        id: event.actor_id,
        name: event.actor_name,
        type: event.actor_type === "user" ? "user" : "api_key",
      });
    }
    return acc;
  }, []);

  return [...userActors, ...apiKeyActors, ...eventActors]
    .filter((actor) => !!actor.name)
    .sort((a, b) => a.name.localeCompare(b.name));
};

export const downloadEventAsCSV = async (event: AuditEvent) => {
  const escapeValue = (value: unknown): string => {
    if (value === null || value === undefined) {
      return "";
    }
    if (typeof value === "object") {
      return `"${JSON.stringify(value).replace(/"/g, '""')}"`;
    }
    if (typeof value === "string") {
      return `"${value.replace(/"/g, '""')}"`;
    }
    return String(value);
  };

  const keys = Object.keys(event) as (keyof AuditEvent)[];
  const csvHeader = keys.join(",");
  const csvRow = keys.map((key) => escapeValue(event[key])).join(",");

  const csv = `${csvHeader}\n${csvRow}`;

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `event_${event.id}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};
